import type React from "react";
import type { ProductPrice, ProductVariant } from "../../types/products";
import Badge from "./Badge";
import Card from "./Card";

interface ProductCardProps {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  sku: string;
  categories: Array<{ id: string; name: string }>;
  isActive: boolean; // Whether product is enabled for listing
  onClick?: (id: string) => void;
  price?: ProductPrice;
  variants?: ProductVariant[];
}

export const ProductCard: React.FC<ProductCardProps> = ({
  id,
  name,
  description,
  imageUrl,
  sku,
  categories,
  isActive,
  onClick,
  price,
  variants,
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick(id);
    }
  };

  const formatPrice = (amount: number | string, currency: string = "USD") => {
    // If already formatted as string (e.g., "$21.92"), return as-is
    if (typeof amount === "string") {
      return amount;
    }
    // Otherwise format the number
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  };

  return (
    <Card
      className="cursor-pointer hover:shadow-lg transition-shadow"
      onClick={handleClick}
    >
      <div className="flex flex-col h-full">
        {imageUrl && (
          <div className="aspect-square w-full mb-4 overflow-hidden rounded-lg bg-gray-100">
            <img
              src={imageUrl}
              alt={name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.src =
                  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="%23e5e7eb"/%3E%3Ctext x="50" y="50" font-family="Arial" font-size="14" fill="%236b7280" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
              }}
            />
          </div>
        )}

        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{name}</h3>
          <p className="text-sm text-gray-500 mb-2">SKU: {sku}</p>

          {description && (
            <p className="text-sm text-gray-600 line-clamp-2 mb-3">
              {description}
            </p>
          )}

          {price && Object.keys(price).length > 0 && (
            <div className="mb-3 overflow-x-auto">
              <table className="min-w-full text-sm">
                <tbody className="divide-y divide-gray-100">
                  {Object.entries(price)
                    .filter(
                      ([_, value]) => value !== undefined && value !== null
                    )
                    .map(([key, value]) => {
                      // Format the key for display - preserve common abbreviations
                      const displayKey = (() => {
                        // Keep common abbreviations intact
                        const commonAbbreviations = ['PC', 'IBO', 'QV'];
                        if (commonAbbreviations.includes(key)) {
                          return key;
                        }
                        // For other keys, add spaces before capitals (but not for the first letter)
                        return key
                          .replace(/([a-z])([A-Z])/g, '$1 $2')
                          .replace(/^./, (str) => str.toUpperCase())
                          .trim();
                      })();

                      // Determine text color based on key
                      let valueClass = "text-gray-900";
                      if (
                        key.toLowerCase().includes("pc") ||
                        key.toLowerCase().includes("preferred")
                      ) {
                        valueClass = "text-green-600 font-medium";
                      } else if (key.toLowerCase().includes("ibo")) {
                        valueClass = "text-blue-600 font-medium";
                      } else if (
                        key.toLowerCase().includes("retail") ||
                        key === "price"
                      ) {
                        valueClass = "text-gray-900 font-semibold";
                      } else if (key.toLowerCase().includes("qv")) {
                        valueClass = "text-purple-600";
                      }

                      // Format the value
                      let displayValue = value;
                      if (typeof value === "boolean") {
                        displayValue = value ? "Yes" : "No";
                      } else if (
                        typeof value === "number" &&
                        !key.toLowerCase().includes("qv") &&
                        !key.toLowerCase().includes("stock")
                      ) {
                        displayValue = formatPrice(
                          value,
                          price.currency || "USD"
                        );
                      }

                      return (
                        <tr key={key} className="hover:bg-gray-50">
                          <td className="py-1 pr-3 text-gray-600 font-medium whitespace-nowrap">
                            {displayKey}:
                          </td>
                          <td className={`py-1 text-right ${valueClass}`}>
                            {String(displayValue)}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}

          {variants && variants.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-gray-500 mb-1">
                {variants.length} variant{variants.length > 1 ? "s" : ""}{" "}
                available
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-1 mb-3">
            {categories.slice(0, 2).map((category) => (
              <Badge key={category.id} variant="neutral" size="sm">
                {category.name}
              </Badge>
            ))}
            {categories.length > 2 && (
              <Badge variant="neutral" size="sm">
                +{categories.length - 2}
              </Badge>
            )}
          </div>
        </div>

        <div className="mt-auto flex gap-2">
          {!isActive && <Badge variant="neutral">Inactive</Badge>}
          {price && price.inStock !== undefined && (
            <Badge variant={price.inStock ? "success" : "danger"}>
              {price.inStock ? "In Stock" : "Out of Stock"}
            </Badge>
          )}
        </div>
      </div>
    </Card>
  );
};
